import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { ROUTES } from '../../lib/constants'

import { loadOnboardingData } from './data'
import { getStepFromPath } from './helpers'
import { OnboardingData } from './types'

// Redirect to step-1 if on base /onboarding path
export function useRedirectToStep1Effect(
  location: ReturnType<typeof useLocation>,
  navigate: ReturnType<typeof useNavigate>
) {
  useEffect(() => {
    if (location.pathname === '/onboarding') {
      navigate(ROUTES.ONBOARDING_STEP_1, { replace: true })
    }
  }, [location.pathname, navigate])
}

// Fix autofill issue: if firstName and lastName are the same, try to correct it
export function useAutofillFixEffect(
  data: OnboardingData,
  setData: React.Dispatch<React.SetStateAction<OnboardingData>>
) {
  useEffect(() => {
    if (data.firstName && data.lastName && data.firstName === data.lastName && data.firstName.trim() !== '') {
      // Chrome might have filled both fields with the same value (first name)
      // Try multiple strategies to get the correct last name
      
      // Strategy 1: Check hidden name field
      const nameField = document.querySelector('input[name="name"]') as HTMLInputElement
      if (nameField && nameField.value && nameField.value.includes(' ')) {
        const parts = nameField.value.trim().split(' ')
        if (parts.length > 1 && parts[0] === data.firstName) {
          const lastName = parts.slice(1).join(' ')
          if (lastName && lastName !== data.firstName) {
            setData(prev => ({
              ...prev,
              lastName: lastName,
            }))
            return
          }
        }
      }
      
      // Strategy 2: Try to extract from email (e.g., r.matsukatov@gmail.com -> Matsukatov)
      if (data.email && data.email.includes('@')) {
        const emailLocal = data.email.split('@')[0]
        // Check if email contains dot (e.g., r.matsukatov)
        if (emailLocal.includes('.')) {
          const emailParts = emailLocal.split('.')
          if (emailParts.length > 1) {
            const possibleLastName = emailParts[emailParts.length - 1]
            // Capitalize first letter
            const capitalizedLastName = possibleLastName.charAt(0).toUpperCase() + possibleLastName.slice(1)
            if (capitalizedLastName !== data.firstName) {
              setData(prev => ({
                ...prev,
                lastName: capitalizedLastName,
              }))
              return
            }
          }
        }
      }
      
      // Strategy 3: If we have user data from Supabase auth, try to use it
      // This will be handled in loadOnboardingData
    }
  }, [data.firstName, data.lastName, data.email, setData])
}

// Debug: Log form fields for autocomplete verification
export function useAutocompleteDebugEffect(currentStep: number) {
  useEffect(() => {
    if (currentStep === 1) {
      // Wait for DOM to update
      setTimeout(() => {
        const firstNameInput = document.getElementById('firstName') as HTMLInputElement
        const lastNameInput = document.getElementById('lastName') as HTMLInputElement
        const emailInput = document.getElementById('email') as HTMLInputElement
        const phoneInput = document.getElementById('phone')
        
        console.log('[AUTOCOMPLETE DEBUG] Form fields check:')
        console.log('First Name:', {
          id: firstNameInput?.id,
          name: firstNameInput?.name,
          autocomplete: firstNameInput?.getAttribute('autocomplete'),
          type: firstNameInput?.type,
          inForm: firstNameInput?.closest('form') !== null
        })
        console.log('Last Name:', {
          id: lastNameInput?.id,
          name: lastNameInput?.name,
          autocomplete: lastNameInput?.getAttribute('autocomplete'),
          type: lastNameInput?.type,
          inForm: lastNameInput?.closest('form') !== null
        })
        console.log('Email:', {
          id: emailInput?.id,
          name: emailInput?.name,
          autocomplete: emailInput?.getAttribute('autocomplete'),
          type: emailInput?.type,
          inForm: emailInput?.closest('form') !== null
        })
        console.log('Phone:', {
          id: phoneInput?.id,
          name: phoneInput?.getAttribute('name'),
          autocomplete: phoneInput?.getAttribute('autocomplete'),
          inForm: phoneInput?.closest('form') !== null
        })
        
        const form = firstNameInput?.closest('form')
        console.log('Form:', {
          autocomplete: form?.getAttribute('autocomplete'),
          method: form?.getAttribute('method')
        })
      }, 100)
    }
  }, [currentStep])
}

// Update step when URL changes
export function useStepFromPathEffect(
  location: ReturnType<typeof useLocation>,
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>
) {
  useEffect(() => {
    const step = getStepFromPath(location.pathname)
    setCurrentStep(step)
  }, [location.pathname, setCurrentStep])
}

// Load existing onboarding data
export function useLoadOnboardingDataEffect(
  dataLoaded: boolean,
  setDataLoaded: React.Dispatch<React.SetStateAction<boolean>>,
  setData: React.Dispatch<React.SetStateAction<OnboardingData>>,
  setResumeFilePath: React.Dispatch<React.SetStateAction<string | null>>,
  setResumeUploaded: React.Dispatch<React.SetStateAction<boolean>>,
  setResumeMdPath: React.Dispatch<React.SetStateAction<string | null>>,
  setAiProvider: React.Dispatch<React.SetStateAction<import('./types').AIProvider>>,
  setAiMemoryFilePath: React.Dispatch<React.SetStateAction<string | null>>,
  setAiMemoryUploaded: React.Dispatch<React.SetStateAction<boolean>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  navigate: ReturnType<typeof useNavigate>,
  data: OnboardingData
) {
  useEffect(() => {
    // Skip if data already loaded (prevents re-fetching when navigating between steps)
    if (dataLoaded) {
      return
    }
    
    const cleanup = loadOnboardingData(
      setData,
      setResumeFilePath,
      setResumeUploaded,
      setResumeMdPath,
      setAiProvider,
      setAiMemoryFilePath,
      setAiMemoryUploaded,
      setDataLoaded,
      setLoading,
      navigate,
      data
    )
    
    return cleanup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount - dataLoaded is managed internally
}
